import * as React from "react";
import ReactHighcharts from "react-highcharts";

import { colors, greys, helpers } from "@styles/color.styles";

import { TimeSeriesProps, ChartTooltipProps } from "../chart/chart";
import {
  highChartsConfig,
  placeholderData,
  placeholderTickPositions,
} from "../chart/chart.constants";
import { formatOrgDataForTimeSeries } from "../overview.util";
import { constructDateRangeTooltip, getTickPositions } from "../chart/chart.util";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";

export interface StackedColumnProps {
  series: TimeSeriesProps;
  tooltip?: ChartTooltipProps;
  accountingPeriodLength: number;
  limit: number;
}

export const StackedColumn = ({
  series,
  tooltip,
  accountingPeriodLength,
  limit,
}: StackedColumnProps) => {
  const { name } = series;
  const withoutData = series.data.length === 0;
  const tickPositions = withoutData ? placeholderTickPositions : undefined;
  const data = withoutData ? placeholderData : formatOrgDataForTimeSeries(series.data);

  const baseData: [number, number][] = [],
    stackedData: [number, number][] = [];
  const plotLineZIndex = 5;
  const columnZIndex = 4;

  data.forEach(([label, value]) => {
    if (value >= limit) {
      baseData.push([label, limit]);
      stackedData.push([label, value - limit]);
    } else {
      baseData.push([label, value]);
      stackedData.push([label, 0]);
    }
  });

  return (
    <ReactHighcharts
      config={{
        ...highChartsConfig,
        tooltip: {
          useHTML: true,
          formatter: function () {
            // To account for incorrect highcharts typings and make TS happy,
            // see more: https://api.highcharts.com/highcharts/tooltip.formatter
            const self: any = this;
            return constructDateRangeTooltip(
              accountingPeriodLength,
              self.point.x,
              self.point.y,
              self.series.name,
              self.series.color,
              tooltip?.valueSuffix,
            );
          },
        },
        chart: { ...highChartsConfig.chart, type: "column" },
        series: [
          { name: `${name} (above free quota)`, data: stackedData, zIndex: columnZIndex },
          { name: `${name} (within free quota)`, data: baseData, zIndex: columnZIndex },
        ],
        plotOptions: {
          column: {
            stacking: "normal",
          },
        },
        colors: [colors.peach.standard, colors.blueCool.lighter],
        xAxis: {
          ...highChartsConfig.xAxis,
          tickPositioner: function () {
            let self: any = this;
            return getTickPositions(baseData, self.tickPositions);
          },
          labels: {
            formatter: function () {
              let self = this;
              if (series.data.length <= 1) {
                return new ZonedMoment(self.value).clone().format("MMM 'YY");
              }
              return self.axis.defaultLabelFormatter.call(self);
            },
          },
        },
        yAxis: {
          ...highChartsConfig.yAxis,
          tickPositions,
          minRange: limit,
          plotLines: [
            {
              value: limit,
              color: colors.peach.standard,
              dashStyle: "shortdot",
              width: 2,
              zIndex: plotLineZIndex,
              label: {
                text: "Free plan quota",
                useHTML: true,
                style: {
                  color: colors.peach.darker,
                  fontWeight: "bold",
                  background: helpers.opacity(greys.white, 0.7),
                  padding: "0px 4px",
                },
              },
            },
          ],
        },
      }}
    />
  );
};
